import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart, registerables } from 'chart.js';
import { ResponsiveTable, ResponsiveTableColumn } from '@/components/common/ResponsiveTable';
import { Badge } from '@/components/common/Badge';
import { Alert } from '@/components/common/Alert';
import { orderService } from '@/services/order.service';
import { Order } from '@/types/order.types';
import { formatPrice } from '@/utils/formatters';
import {
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  EyeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import '@/styles/dashboard.css';

Chart.register(...registerables);

export const Transaction = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const itemsPerPage = 10;
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  // Filtres
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState<string>('');
  const [filterMinAmount, setFilterMinAmount] = useState<string>('');
  const [filterMaxAmount, setFilterMaxAmount] = useState<string>('');

  // Données
  const [orders, setOrders] = useState<Order[]>([]);
  const [allOrders, setAllOrders] = useState<Order[]>([]); // Toutes les commandes pour les stats
  const [detailModalOpen, setDetailModalOpen] = useState<Order | null>(null);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    completed: 0,
    pending: 0,
    failed: 0,
    revenueChange: 0,
    completedChange: 0,
  });
  const [chartData, setChartData] = useState<{ labels: string[]; data: number[] }>({
    labels: [],
    data: [],
  });

  // Charger toutes les commandes pour les statistiques
  useEffect(() => {
    loadAllOrdersForStats();
  }, []);

  // Charger les données filtrées pour le tableau
  useEffect(() => {
    loadOrders();
  }, [activeTab, filterDateFrom, filterDateTo, filterPaymentStatus]);

  useEffect(() => {
    calculateStats();
    calculateChartData();
  }, [allOrders]);

  const loadAllOrdersForStats = async () => {
    try {
      // Charger toutes les commandes sans filtre pour les statistiques
      const data = await orderService.getAll();
      console.log('📊 Transactions - Commandes chargées pour stats:', data.length);
      if (data.length > 0) {
        console.log('📊 Exemple de commande:', {
          id: data[0].id,
          created_at: data[0].created_at,
          payment_status: data[0].payment_status,
          order_status: data[0].order_status,
          total: data[0].total,
        });
      }
      setAllOrders(data);
    } catch (err: any) {
      console.error('Erreur lors du chargement de toutes les commandes:', err);
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters: any = {};
      
      if (activeTab !== 'all') {
        if (activeTab === 'completed') {
          filters.status = 'delivered';
        } else {
          filters.status = activeTab;
        }
      }

      if (filterDateFrom) {
        filters.date_from = filterDateFrom + ' 00:00:00';
      }
      if (filterDateTo) {
        filters.date_to = filterDateTo + ' 23:59:59';
      }
      if (filterPaymentStatus) {
        filters.payment_status = filterPaymentStatus;
      }

      const data = await orderService.getAll(filters);
      setOrders(data);
    } catch (err: any) {
      console.error('Erreur lors du chargement des transactions:', err);
      setError(t('adminTransaction.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    if (allOrders.length === 0) {
      console.log('⚠️ Aucune commande chargée pour calculer les statistiques');
      setStats({
        totalRevenue: 0,
        completed: 0,
        pending: 0,
        failed: 0,
        revenueChange: 0,
        completedChange: 0,
      });
      return;
    }

    const now = new Date();
    now.setHours(23, 59, 59, 999); // Fin de la journée aujourd'hui
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    sevenDaysAgo.setHours(0, 0, 0, 0); // Début de la journée il y a 7 jours
    const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    fourteenDaysAgo.setHours(0, 0, 0, 0);

    console.log('📅 Dates de calcul:', {
      now: now.toISOString(),
      sevenDaysAgo: sevenDaysAgo.toISOString(),
      fourteenDaysAgo: fourteenDaysAgo.toISOString(),
    });

    // Calculer les revenus et transactions pour les 7 derniers jours
    const last7Days = allOrders.filter(order => {
      if (!order.created_at) return false;
      try {
        const orderDate = new Date(order.created_at);
        return orderDate >= sevenDaysAgo && orderDate <= now;
      } catch (e) {
        console.error('Erreur parsing date:', order.created_at, e);
        return false;
      }
    });

    // Calculer les revenus et transactions pour les 7 jours précédents
    const previous7Days = allOrders.filter(order => {
      if (!order.created_at) return false;
      try {
        const orderDate = new Date(order.created_at);
        return orderDate >= fourteenDaysAgo && orderDate < sevenDaysAgo;
      } catch (e) {
        console.error('Erreur parsing date:', order.created_at, e);
        return false;
      }
    });

    // Revenu total (7 derniers jours) - toutes les transactions payées
    const totalRevenue = last7Days
      .filter(order => order.payment_status === 'paid')
      .reduce((sum, order) => sum + (order.total || 0), 0);

    // Revenu précédent (7 jours précédents)
    const previousRevenue = previous7Days
      .filter(order => order.payment_status === 'paid')
      .reduce((sum, order) => sum + (order.total || 0), 0);

    // Calcul du pourcentage de changement
    const revenueChange = previousRevenue > 0 
      ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 
      : (totalRevenue > 0 ? 100 : 0);

    // Transactions terminées (delivered) - 7 derniers jours
    const completed = last7Days.filter(order => order.order_status === 'delivered').length;
    const previousCompleted = previous7Days.filter(order => order.order_status === 'delivered').length;
    const completedChange = previousCompleted > 0 
      ? ((completed - previousCompleted) / previousCompleted) * 100 
      : (completed > 0 ? 100 : 0);

    // Transactions en attente - 7 derniers jours
    const pending = last7Days.filter(order => 
      order.order_status === 'pending' || order.order_status === 'processing'
    ).length;

    // Transactions échouées (cancelled ou unpaid) - 7 derniers jours
    const failed = last7Days.filter(order => 
      order.order_status === 'cancelled' || order.payment_status === 'unpaid'
    ).length;

    // Si aucune donnée dans les 7 derniers jours, utiliser toutes les données
    let finalRevenue = totalRevenue;
    let finalCompleted = completed;
    let finalPending = pending;
    let finalFailed = failed;

    if (last7Days.length === 0 && allOrders.length > 0) {
      // Utiliser toutes les commandes si aucune dans les 7 derniers jours
      console.log('⚠️ Aucune commande dans les 7 derniers jours, utilisation de toutes les commandes');
      finalRevenue = allOrders
        .filter(order => order.payment_status === 'paid')
        .reduce((sum, order) => sum + (order.total || 0), 0);
      finalCompleted = allOrders.filter(order => order.order_status === 'delivered').length;
      finalPending = allOrders.filter(order => 
        order.order_status === 'pending' || order.order_status === 'processing'
      ).length;
      finalFailed = allOrders.filter(order => 
        order.order_status === 'cancelled' || order.payment_status === 'unpaid'
      ).length;
    }

    console.log('📊 Calcul des statistiques:', {
      totalOrders: allOrders.length,
      last7Days: last7Days.length,
      previous7Days: previous7Days.length,
      totalRevenue: finalRevenue,
      previousRevenue,
      completed: finalCompleted,
      pending: finalPending,
      failed: finalFailed,
      revenueChange: Math.round(revenueChange * 10) / 10,
      completedChange: Math.round(completedChange * 10) / 10,
    });

    setStats({
      totalRevenue: finalRevenue,
      completed: finalCompleted,
      pending: finalPending,
      failed: finalFailed,
      revenueChange: Math.round(revenueChange * 10) / 10,
      completedChange: Math.round(completedChange * 10) / 10,
    });
  };

  const calculateChartData = () => {
    if (allOrders.length === 0) {
      // Même sans commandes, afficher un graphique vide pour les 7 derniers jours
      const now = new Date();
      const labels: string[] = [];
      const data: number[] = [];
      const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        labels.push(dayNames[date.getDay()]);
        data.push(0);
      }
      setChartData({ labels, data });
      return;
    }

    const now = new Date();
    now.setHours(23, 59, 59, 999);
    const labels: string[] = [];
    const data: number[] = [];

    // Calculer les revenus pour les 7 derniers jours
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);
      nextDate.setHours(0, 0, 0, 0);

      const dayOrders = allOrders.filter(order => {
        if (!order.created_at) return false;
        try {
          const orderDate = new Date(order.created_at);
          return orderDate >= date && orderDate < nextDate && order.payment_status === 'paid';
        } catch (e) {
          return false;
        }
      });

      const dayRevenue = dayOrders.reduce((sum, order) => sum + (order.total || 0), 0);

      // Nom du jour
      const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
      labels.push(dayNames[date.getDay()]);
      data.push(dayRevenue);
    }

    // Si aucune donnée n'est trouvée dans les 7 derniers jours, utiliser les 30 derniers jours
    // et afficher les 7 derniers jours qui contiennent des données
    if (data.every(val => val === 0) && allOrders.length > 0) {
      console.log('⚠️ Aucune commande trouvée dans les 7 derniers jours. Utilisation des 30 derniers jours.');
      
      // Calculer les revenus pour les 30 derniers jours
      const revenueByDay: Record<string, number> = {};
      const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
      
      for (let i = 29; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        
        const nextDate = new Date(date);
        nextDate.setDate(nextDate.getDate() + 1);
        nextDate.setHours(0, 0, 0, 0);

        const dayOrders = allOrders.filter(order => {
          if (!order.created_at) return false;
          try {
            const orderDate = new Date(order.created_at);
            return orderDate >= date && orderDate < nextDate && order.payment_status === 'paid';
          } catch (e) {
            return false;
          }
        });

        const dayRevenue = dayOrders.reduce((sum, order) => sum + (order.total || 0), 0);
        const dayKey = date.toISOString().split('T')[0];
        revenueByDay[dayKey] = dayRevenue;
      }

      // Trouver les 7 derniers jours qui contiennent des données
      const daysWithData = Object.entries(revenueByDay)
        .filter(([_, revenue]) => revenue > 0)
        .sort(([dateA], [dateB]) => dateB.localeCompare(dateA))
        .slice(0, 7);

      if (daysWithData.length > 0) {
        // Utiliser les 7 derniers jours avec des données
        const newLabels: string[] = [];
        const newData: number[] = [];
        
        daysWithData.forEach(([dateKey, revenue]) => {
          const date = new Date(dateKey);
          newLabels.push(dayNames[date.getDay()]);
          newData.push(revenue);
        });

        // Si on a moins de 7 jours, compléter avec des zéros
        while (newLabels.length < 7) {
          newLabels.unshift(dayNames[new Date().getDay()]);
          newData.unshift(0);
        }

        console.log('📈 Données du graphique (fallback 30 jours):', { labels: newLabels, data: newData });
        setChartData({ labels: newLabels, data: newData });
        return;
      }
    }

    console.log('📈 Données du graphique:', { labels, data, totalRevenue: stats.totalRevenue });
    setChartData({ labels, data });
  };

  // Filtrer les transactions
  const filteredOrders = orders.filter(order => {
    // Filtre par recherche
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        order.order_number.toLowerCase().includes(search) ||
        order.customer_name.toLowerCase().includes(search) ||
        order.customer_email.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Filtre par montant (côté client)
    if (filterMinAmount && order.total < parseFloat(filterMinAmount)) {
      return false;
    }
    if (filterMaxAmount && order.total > parseFloat(filterMaxAmount)) {
      return false;
    }

    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, filterDateFrom, filterDateTo, filterPaymentStatus, filterMinAmount, filterMaxAmount]);

  // Créer/mettre à jour le graphique
  useEffect(() => {
    if (!chartRef.current) return;
    
    // Toujours créer le graphique, même si les données sont vides
    if (chartData.labels.length === 0) {
      // Réinitialiser les données du graphique si vide
      calculateChartData();
      return;
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Détruire l'ancien graphique s'il existe
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    const maxData = Math.max(...chartData.data, 1);
    const maxValue = Math.max(Math.ceil(maxData * 1.2), 100);

    chartInstanceRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: chartData.labels,
        datasets: [{
          label: 'Revenu (€)',
          data: chartData.data,
          borderColor: '#8C1843',
          backgroundColor: 'rgba(140, 24, 67, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#8C1843',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: {
              size: 14,
              weight: 'bold'
            },
            bodyFont: {
              size: 12
            },
            callbacks: {
              label: function(context) {
                const value = context.parsed.y;
                return `${value.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}`;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            max: maxValue,
            ticks: {
              callback: function(value) {
                return `${value.toLocaleString('fr-FR')} €`;
              },
              font: {
                size: 11
              },
              color: '#7C7C7C'
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          x: {
            ticks: {
              font: {
                size: 11
              },
              color: '#7C7C7C'
            },
            grid: {
              display: false
            }
          }
        },
        animation: {
          duration: 800,
          easing: 'easeOutQuart'
        }
      }
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [chartData]);

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; icon: any; variant: 'success' | 'error' | 'warning' | 'info' | 'neutral' }> = {
      delivered: {
        label: t('adminTransaction.statusComplete'),
        icon: CheckCircleIcon,
        variant: 'success',
      },
      pending: {
        label: t('adminTransaction.pending'),
        icon: ClockIcon,
        variant: 'warning',
      },
      processing: {
        label: t('adminTransaction.processing'),
        icon: ClockIcon,
        variant: 'info',
      },
      shipped: {
        label: t('adminTransaction.shipped'),
        icon: ClockIcon,
        variant: 'info',
      },
      cancelled: {
        label: t('adminTransaction.cancelled'),
        icon: XCircleIcon,
        variant: 'error',
      },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
    <>

      <Badge variant={config.variant}>
        <Icon className="btn-icon" style={{ width: '14px', height: '14px' }} />
        {config.label}
      </Badge>
    
    </>
  );
  };

  const getPaymentBadge = (paymentStatus: string) => {
    const variant = paymentStatus === 'paid' ? 'success' : paymentStatus === 'pending' ? 'warning' : 'error';
    const label = paymentStatus === 'paid' ? t('adminTransaction.paid') : paymentStatus === 'pending' ? t('adminTransaction.pending') : t('adminTransaction.unpaid');
    return (
    <>

      <Badge variant={variant}>
        • {label}
      </Badge>
    
    </>
  );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const columns: ResponsiveTableColumn<Order>[] = [
    {
      key: 'order_number',
      label: t('adminTransaction.orderNumber'),
      render: (order) => <div className="table-cell-name"><strong>{order.order_number}</strong></div>,
    },
    {
      key: 'customer',
      label: t('adminInvoices.client'),
      render: (order) => (
        <div>
          <div className="table-cell-name">{order.customer_name}</div>
          <div className="text-xs text-body">{order.customer_email}</div>
        </div>
      ),
    },
    {
      key: 'date',
      label: t('adminTransaction.date'),
      render: (order) => <strong>{formatDate(order.created_at)}</strong>,
    },
    {
      key: 'total',
      label: t('adminTransaction.total'),
      render: (order) => <strong>{formatPrice(order.total)}</strong>,
    },
    {
      key: 'status',
      label: t('adminTransaction.status'),
      render: (order) => getStatusBadge(order.order_status),
    },
    {
      key: 'actions',
      label: t('adminTransaction.actions'),
      mobileHidden: true,
      render: (order) => (
        <div className="product-actions">
          <button
            className="action-btn action-view"
            onClick={() => setDetailModalOpen(order)}
            title={t('adminTransaction.viewDetails')}
            aria-label={t('adminTransaction.viewDetailsAria')}
          >
            <EyeIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>

    {error && (
              <Alert
                type="error"
                message={error}
                onClose={() => setError(null)}
              />
            )}

            {/* KPI Cards Row */}
            <div className="kpi-row">
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <CurrencyDollarIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#10B981' }} />
                  <h3 className="kpi-title">{t('adminTransaction.totalRevenue')}</h3>
                  </div>
                  <span className="kpi-index">#1</span>
                </div>
                <div className="kpi-value">
                  {loading ? '...' : stats.totalRevenue.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                </div>
                <div className="kpi-change positive">
                  <span className="change-icon">↑</span>
                  <span className="change-value">{stats.revenueChange.toFixed(1)}%</span>
                </div>
                <div className="kpi-period">{t('adminTransaction.previous7Days')}</div>
              </div>

              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <CheckCircleIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#10B981' }} />
                  <h3 className="kpi-title">{t('adminTransaction.completedTransactions')}</h3>
                  </div>
                  <span className="kpi-index">#2</span>
                </div>
                <div className="kpi-value">{loading ? '...' : stats.completed.toLocaleString('fr-FR')}</div>
                <div className="kpi-change positive">
                  <span className="change-icon">↑</span>
                  <span className="change-value">{stats.completedChange.toFixed(1)}%</span>
                </div>
                <div className="kpi-period">{t('adminTransaction.previous7Days')}</div>
              </div>

              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <ClockIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#F59E0B' }} />
                  <h3 className="kpi-title">{t('adminTransaction.pendingTransactions')}</h3>
                  </div>
                  <span className="kpi-index">#3</span>
                </div>
                <div className="kpi-value">{loading ? '...' : stats.pending.toLocaleString('fr-FR')}</div>
                <div className="kpi-change">
                  <span className="change-value">85%</span>
                </div>
                <div className="kpi-period">{t('adminTransaction.previous7Days')}</div>
              </div>

              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <XCircleIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#EF4444' }} />
                  <h3 className="kpi-title">{t('adminTransaction.failedTransactions')}</h3>
                  </div>
                  <span className="kpi-index">#4</span>
                </div>
                <div className="kpi-value">{loading ? '...' : stats.failed.toLocaleString('fr-FR')}</div>
                <div className="kpi-change">
                  <span className="change-value">15%</span>
                </div>
                <div className="kpi-period">{t('adminTransaction.previous7Days')}</div>
              </div>
            </div>

            {/* Graphique Revenu total */}
            <div className="table-card" style={{ marginBottom: '24px' }}>
              <div className="table-header">
                <h3 className="table-title">{t('adminTransaction.revenueEvolution')}</h3>
                    </div>
              <div style={{ padding: '20px', height: '300px' }}>
                {chartData.labels.length > 0 ? (
                  <canvas ref={chartRef} id="revenueChart"></canvas>
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    color: '#666',
                    fontSize: '14px'
                  }}>
                    {t('adminTransaction.chartLoading')}
                  </div>
                )}
              </div>
            </div>

            {/* Transactions Table */}
            <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">{t('adminTransaction.transactions', { count: filteredOrders.length })}</h3>
                <div className="flex gap-2 items-center">
                  {(searchTerm || filterDateFrom || filterDateTo || filterPaymentStatus || filterMinAmount || filterMaxAmount) && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilterDateFrom('');
                        setFilterDateTo('');
                        setFilterPaymentStatus('');
                        setFilterMinAmount('');
                        setFilterMaxAmount('');
                      }}
                      className="btn btn-secondary btn-sm"
                    >
                      {t('adminTransaction.resetFilters')}
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Tabs */}
              <div className="orders-tabs" style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #e0e0e0' }}>
                <button
                  className={`order-tab ${activeTab === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveTab('all')}
                >
                  {t('adminTransaction.allTransactions')} <span className="tab-count">({orders.length})</span>
                </button>
                <button
                  className={`order-tab ${activeTab === 'completed' ? 'active' : ''}`}
                  onClick={() => setActiveTab('completed')}
                >
                  {t('adminTransaction.completed')} <span className="tab-count">({orders.filter(o => o.order_status === 'delivered').length})</span>
                </button>
                <button
                  className={`order-tab ${activeTab === 'pending' ? 'active' : ''}`}
                  onClick={() => setActiveTab('pending')}
                >
                  {t('adminTransaction.pending')} <span className="tab-count">({orders.filter(o => o.order_status === 'pending' || o.order_status === 'processing').length})</span>
                </button>
                <button
                  className={`order-tab ${activeTab === 'cancelled' ? 'active' : ''}`}
                  onClick={() => setActiveTab('cancelled')}
                >
                  {t('adminTransaction.cancelled')} <span className="tab-count">({orders.filter(o => o.order_status === 'cancelled').length})</span>
                </button>
              </div>

              {/* Search Bar & Filters */}
              <div className="filters-bar">
                <div className="form-group-flex">
                  <label className="form-label">{t('adminTransaction.search')}</label>
                  <input
                    type="text"
                    placeholder={t('adminTransaction.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminTransaction.dateFrom')}</label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminTransaction.dateTo')}</label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminTransaction.paymentStatus')}</label>
                  <select
                    value={filterPaymentStatus}
                    onChange={(e) => setFilterPaymentStatus(e.target.value)}
                    className="form-select"
                  >
                    <option value="">{t('adminTransaction.all')}</option>
                    <option value="paid">{t('adminTransaction.paid')}</option>
                    <option value="pending">{t('adminTransaction.pending')}</option>
                    <option value="unpaid">{t('adminTransaction.unpaid')}</option>
                  </select>
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminTransaction.minAmount')}</label>
                  <input
                    type="number"
                    placeholder={t('adminTransaction.placeholder0')}
                    value={filterMinAmount}
                    onChange={(e) => setFilterMinAmount(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminTransaction.maxAmount')}</label>
                  <input
                    type="number"
                    placeholder={t('adminTransaction.placeholderInf')}
                    value={filterMaxAmount}
                    onChange={(e) => setFilterMaxAmount(e.target.value)}
                    className="form-input"
                  />
                </div>
                {(filterDateFrom || filterDateTo || filterPaymentStatus || filterMinAmount || filterMaxAmount) && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setFilterDateFrom('');
                      setFilterDateTo('');
                      setFilterPaymentStatus('');
                      setFilterMinAmount('');
                      setFilterMaxAmount('');
                    }}
                  >
                    {t('adminTransaction.reset')}
                </button>
                )}
              </div>

              <ResponsiveTable
                columns={columns}
                data={paginatedOrders}
                loading={loading}
                loadingMessage={t('adminTransaction.loading')}
                emptyMessage={
                  searchTerm || activeTab !== 'all' || filterDateFrom || filterDateTo || filterPaymentStatus || filterMinAmount || filterMaxAmount
                    ? t('adminTransaction.noFilters')
                    : t('adminTransaction.empty')
                }
                mobileCardTitle={(order) => (
                  <div>
                    <div className="table-cell-name mb-1"><strong>{order.order_number}</strong></div>
                    <div className="text-xs text-body">{order.customer_name}</div>
                  </div>
                )}
                mobileCardActions={(order) => (
                  <div className="product-actions" style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="action-btn action-view"
                      onClick={() => setDetailModalOpen(order)}
                      title={t('adminTransaction.viewDetails')}
                      aria-label={t('adminTransaction.viewDetailsAria')}
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
                tableClassName="products-table"
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-container" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '24px',
                  padding: '16px',
                  borderTop: '1px solid #e0e0e0'
                }}>
                  <div className="pagination-info" style={{ fontSize: '14px', color: '#666' }}>
                    {t('adminTransaction.displaying', { start: startIndex + 1, end: Math.min(endIndex, filteredOrders.length), total: filteredOrders.length })}
                  </div>
                  <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      {t('adminTransaction.previous')}
                    </button>
                    <div className="pagination-pages" style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              className={`btn ${currentPage === page ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                              onClick={() => setCurrentPage(page)}
                              style={{ minWidth: '36px' }}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '0 4px' }}>...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      {t('adminTransaction.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Modal de détail de transaction */}
            {detailModalOpen && (
              <div 
                className="modal-overlay" 
                onClick={() => setDetailModalOpen(null)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="detail-modal-title"
              >
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
                  <div className="modal-header">
                    <h3 id="detail-modal-title" className="modal-title">
                      {t('adminTransaction.transactionDetails', { number: detailModalOpen.order_number })}
                    </h3>
                    <button 
                      className="modal-close"
                      onClick={() => setDetailModalOpen(null)}
                      aria-label={t('adminTransaction.close')}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>
                  <div className="modal-body">
                    {(() => {
                      const order = detailModalOpen;
                      const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
                      
                      return (
    <>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                          {/* Informations client */}
                          <div>
                            <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                              {t('adminTransaction.clientInfo')}
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                              <div>
                                <strong>{t('adminTransaction.nameLabel')}</strong> {order.customer_name}
                              </div>
                              <div>
                                <strong>{t('adminTransaction.emailLabel')}</strong> {order.customer_email}
                              </div>
                              <div>
                                <strong>{t('adminTransaction.phoneLabel')}</strong> {order.customer_phone || t('adminTransaction.na')}
                              </div>
                              <div>
                                <strong>{t('adminTransaction.cityLabel')}</strong> {order.customer_city || t('adminTransaction.na')}
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <strong>{t('adminTransaction.addressLabel')}</strong> {order.customer_address || t('adminTransaction.na')}
                              </div>
                              {order.customer_postal_code && (
                                <div>
                                  <strong>{t('adminTransaction.postalCodeLabel')}</strong> {order.customer_postal_code}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Produits */}
                          <div>
                            <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                              {t('adminTransaction.products')} ({items.length})
                            </h4>
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ background: '#f5f5f5' }}>
                                  <tr>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>{t('adminTransaction.product')}</th>
                                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>{t('adminTransaction.quantity')}</th>
                                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>{t('adminTransaction.unitPrice')}</th>
                                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>{t('adminTransaction.total')}</th>
                  </tr>
                </thead>
                <tbody>
                                  {items.map((item: any, index: number) => (
                                    <tr key={index} style={{ borderTop: '1px solid #e0e0e0' }}>
                                      <td style={{ padding: '12px' }}>{item.product_name || t('adminTransaction.product')}</td>
                                      <td style={{ padding: '12px', textAlign: 'center' }}>{item.quantity || 1}</td>
                                      <td style={{ padding: '12px', textAlign: 'right' }}>{formatPrice(item.price || 0)}</td>
                                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>{formatPrice(item.total || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
                            </div>
                          </div>

                          {/* Totaux */}
                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <div style={{ width: '300px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{t('adminTransaction.subtotal')}</span>
                                <strong>{formatPrice(order.subtotal || 0)}</strong>
                              </div>
                              {order.shipping_cost > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span>{t('adminTransaction.shippingCost')}</span>
                                  <strong>{formatPrice(order.shipping_cost)}</strong>
                                </div>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '2px solid #333', fontSize: '18px', fontWeight: '700' }}>
                                <span>{t('adminTransaction.totalLabel')}</span>
                                <strong>{formatPrice(order.total)}</strong>
                              </div>
                            </div>
                          </div>

                          {/* Statuts et dates */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                            <div>
                              <strong>{t('adminTransaction.transactionStatus')}</strong>
                              <div style={{ marginTop: '8px' }}>
                                {getStatusBadge(order.order_status)}
                              </div>
                            </div>
                            <div>
                              <strong>{t('adminTransaction.paymentStatusLabel')}</strong>
                              <div style={{ marginTop: '8px' }}>
                                {getPaymentBadge(order.payment_status)}
                              </div>
                            </div>
                            <div>
                              <strong>{t('adminTransaction.createdAtLabel')}</strong>
                              <div style={{ marginTop: '4px', color: '#666' }}>
                                {formatDate(order.created_at)}
                              </div>
                            </div>
                            {order.updated_at && order.updated_at !== order.created_at && (
                              <div>
                                <strong>{t('adminTransaction.lastModifiedLabel')}</strong>
                                <div style={{ marginTop: '4px', color: '#666' }}>
                                  {formatDate(order.updated_at)}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      
    </>
  );
                    })()}
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setDetailModalOpen(null)}
                    >
                      {t('adminTransaction.close')}
                    </button>
                  </div>
                </div>
              </div>
            )}
  
    </>
  );
};
